/**
 * Step 7: 설정 확인
 */

import React from 'react';
import { ChunkingConfig } from '../../../api';
import { EMBEDDING_MODELS } from '../../../api';
import { SourceType, SOURCE_LABELS, CHUNK_STRATEGIES, WebConfig, DbConfig } from '../shared/pipeline.types';
import { Badge } from '../../ui';
import InfoIcon from '../../../assets/icons/common/info.svg?react';
import DocumentIcon from '../../../assets/icons/dashboard/document.svg?react';
import SearchIcon from '../../../assets/icons/pipeline/step-search.svg?react';
import SimilarityIcon from '../../../assets/icons/common/similarity.svg?react';
import ChunkIcon from '../../../assets/icons/chunking/fixed.svg?react';

interface ReviewStepProps {
  kbName: string;
  kbDescription: string;
  activeSources: SourceType[];
  hasFile: boolean;
  files: File[];
  driveFiles?: { fileId: string; fileName: string }[];
  dropboxFiles?: { filePath: string; fileName: string }[];
  fileChunking: ChunkingConfig;
  hasWeb: boolean;
  webConfig: WebConfig;
  webChunking: ChunkingConfig;
  hasDatabase: boolean;
  dbConfig: DbConfig;
  dbChunking: ChunkingConfig;
  embeddingModelId: string;
  topK: number;
  similarityThreshold: number;
}

function chunkingLabel(config: ChunkingConfig, sourceKey: SourceType): string {
  const strategies = CHUNK_STRATEGIES[sourceKey];
  const found = strategies.find((s) => s.value === config.strategy);
  const title = found?.title ?? config.strategy;
  if (config.strategy === 'fixed' || config.strategy === 'paragraph') {
    return `${title} (${config.chunkSize}토큰, 오버랩 ${config.chunkOverlap})`;
  }
  return title;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="review-row">
      <div className="review-row-label">{label}</div>
      <div className="review-row-value">{children}</div>
    </div>
  );
}

// 위쪽 요약 카드 - 대시보드 통계 카드(.item-card + dash-stat)와 같은 구성
function StatCard({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="item-card review-stat">
      <span className="item-card-icon item-card-icon--primary">{icon}</span>
      <div className="item-card-body">
        <div className="review-stat-label">{label}</div>
        {children}
      </div>
    </div>
  );
}

export function ReviewStep({
  kbName, kbDescription, activeSources,
  hasFile, files, driveFiles = [], dropboxFiles = [], fileChunking,
  hasWeb, webConfig, webChunking,
  hasDatabase, dbConfig, dbChunking,
  embeddingModelId, topK, similarityThreshold,
}: ReviewStepProps) {
  const hintMsg =
    activeSources.length > 1
      ? '"게시하기"를 누르면 각 소스의 처리가 시작됩니다.'
      : hasFile
      ? '"게시하기"를 누르면 파일이 업로드되고 문서 처리가 시작됩니다.'
      : hasWeb
      ? '"게시하기"를 누르면 웹 크롤링이 시작됩니다.'
      : '"게시하기"를 누르면 DB 연결이 설정되고 데이터 동기화가 시작됩니다.';

  const embeddingName = EMBEDDING_MODELS.find((m) => m.id === embeddingModelId)?.name ?? embeddingModelId;
  const similarityPercent = Number((similarityThreshold * 100).toFixed(0));

  // 첫 요약 카드 - 소스가 여러 개면 소스 수, 하나면 그 소스의 수량
  const sourceStat =
    activeSources.length > 1
      ? { label: '데이터 소스', value: activeSources.length, unit: '개' }
      : hasFile
      ? { label: '파일', value: files.length + driveFiles.length + dropboxFiles.length, unit: '개' }
      : hasWeb
      ? { label: '크롤링 깊이', value: webConfig.depth, unit: '단계' }
      : { label: 'DB 테이블', value: dbConfig.tables.length, unit: '개' };

  // 네 번째 요약 카드 - 청킹 전략 이름 (+ 고정 크기 · 문단 단위면 크기 · 오버랩)
  // 소스가 여러 개면 첫 번째 소스 기준, 라벨에 소스 이름 표시
  const chunkSource = activeSources[0] ?? 'file';
  const chunkConfig = { file: fileChunking, web: webChunking, database: dbChunking }[chunkSource];
  const chunkStat = {
    label: activeSources.length > 1 ? `청킹 (${SOURCE_LABELS[chunkSource]})` : '청킹',
    title: CHUNK_STRATEGIES[chunkSource].find((s) => s.value === chunkConfig.strategy)?.title ?? chunkConfig.strategy,
    detail:
      chunkConfig.strategy === 'fixed' || chunkConfig.strategy === 'paragraph'
        ? `${chunkConfig.chunkSize}토큰 · 오버랩 ${chunkConfig.chunkOverlap}`
        : '',
  };

  // 청킹 행 라벨 - 소스가 여러 개면 어느 소스인지 표시
  const chunkRowLabel = (s: SourceType) =>
    activeSources.length > 1 ? `청킹 (${SOURCE_LABELS[s]})` : '청킹';

  return (
    <div className="pipe-section form-item--md">
      <div className="form-label">
        설정 확인
        <span className="optional">
          아래 설정을 확인하고 "게시하기"를 눌러 지식베이스를 생성하세요.
        </span>
      </div>

      {/* 요약 카드 4개 - 데이터 · 검색 결과 수 · 유사도 · 임베딩 모델 */}
      <div className="review-stats">
        <StatCard icon={<DocumentIcon />} label={sourceStat.label}>
          <div className="review-stat-value">{sourceStat.value}<small>{sourceStat.unit}</small></div>
        </StatCard>
        <StatCard icon={<SearchIcon />} label="검색 결과 수">
          <div className="review-stat-value">{topK}<small>개</small></div>
        </StatCard>
        <StatCard icon={<SimilarityIcon />} label="유사도">
          <div className="review-stat-value">{similarityPercent}<small>%</small></div>
          <div className="review-stat-bar"><i style={{ width: `${similarityPercent}%` }} /></div>
        </StatCard>
        <StatCard icon={<ChunkIcon />} label={chunkStat.label}>
          <div className="review-stat-text">{chunkStat.title}</div>
          {chunkStat.detail && <div className="review-stat-sub">{chunkStat.detail}</div>}
        </StatCard>
      </div>

      {/* 상세 설정 */}
      <div className="review-detail">
        <div className="review-detail-title">상세 설정</div>
        <div className="review-summary-card">
          <Row label="지식베이스 이름">
            {kbName}
          </Row>

          {kbDescription && <Row label="설명">{kbDescription}</Row>}

          <Row label="데이터 소스">
            <div className="review-source-badges">
              {activeSources.map((s) => (
                // 데이터 소스 종류 배지 - 모달 헤더 · 지식베이스 목록과 같은 source-* 색
                <Badge key={s} variant={`source-${s}`} shape='tag'>{SOURCE_LABELS[s]}</Badge>
              ))}
            </div>
          </Row>

          {/* 소스별 정보 + 청킹 (소스가 여러 개면 청킹 라벨에 소스 이름) */}
          {hasFile && (
            <>
              <Row label="파일">{files.length + driveFiles.length + dropboxFiles.length}개 파일</Row>
              <Row label={chunkRowLabel('file')}>{chunkingLabel(fileChunking, 'file')}</Row>
            </>
          )}

          {hasWeb && (
            <>
              <Row label="웹 크롤링">{webConfig.url} (깊이 {webConfig.depth})</Row>
              <Row label={chunkRowLabel('web')}>{chunkingLabel(webChunking, 'web')}</Row>
            </>
          )}

          {hasDatabase && (
            <>
              <Row label="DB">
                {dbConfig.type.toUpperCase()} {dbConfig.host}:{dbConfig.port}/{dbConfig.database}
              </Row>
              <Row label={chunkRowLabel('database')}>{chunkingLabel(dbChunking, 'database')}</Row>
            </>
          )}

          <Row label="임베딩 모델">{embeddingName}</Row>

          <Row label="검색 설정">
            Top {topK}개, 유사도 {similarityPercent}% 이상
          </Row>
        </div>
      </div>

      <div className="review-save-guide">
        <InfoIcon />
        {hintMsg}
      </div>
    </div>
  );
}
